import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/Textarea';
import { 
  FileText, 
  MessageSquare, 
  Loader2, 
  Clock, 
  User, 
  Users, 
  Calendar,
  AlertCircle,
  CheckCircle,
  XCircle,
  Download,
  Lock,
  X,
  Target
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const TaskDetailsModal = ({ 
  isOpen, 
  onClose, 
  selectedTask, 
  onAddComment, 
  onDownloadFile,
  submitting = false,
  showCustomToast
}) => {
  const [newCommentText, setNewCommentText] = useState('');
  const textareaRef = useRef(null);

  // ✅ RÉINITIALISATION quand le modal change ou se ferme
  useEffect(() => {
    if (!isOpen) {
      setNewCommentText('');
    }
  }, [isOpen, selectedTask?.id]);

  // ============================================================================
  // GESTIONNAIRES D'ÉVÉNEMENTS
  // ============================================================================

  const handleTextChange = useCallback((e) => {
    const text = e.target.value;
    if (text.length <= 500) {
      setNewCommentText(text);
    }
  }, []);

  const handleAddComment = useCallback(async () => {
    if (!newCommentText.trim() || !selectedTask) return;
    
    try {
      // Afficher toast loading
      showCustomToast('Ajout du commentaire en cours...', 'loading');
      
      await onAddComment(selectedTask.id, newCommentText.trim());
      
      setNewCommentText('');
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    } catch (error) {
      console.error('Erreur ajout commentaire:', error);
    }
  }, [newCommentText, selectedTask, onAddComment, showCustomToast]);

  const handleDownloadFile = useCallback((filename) => {
    if (onDownloadFile && selectedTask) {
      onDownloadFile(selectedTask.id, filename);
    }
  }, [onDownloadFile, selectedTask]);

  // ============================================================================
  // FONCTIONS UTILITAIRES
  // ============================================================================

  const getStatutBadge = (statut) => {
    const configs = {
      nouveau: { 
        icon: Clock, 
        text: "Nouveau", 
        bg: "bg-blue-100", 
        color: "text-blue-800"
      },
      en_cours: { 
        icon: Target, 
        text: "En cours", 
        bg: "bg-teal-100", 
        color: "text-teal-800"
      },
      termine: { 
        icon: CheckCircle, 
        text: "Terminée", 
        bg: "bg-green-100", 
        color: "text-green-800"
      },
      terminee: { 
        icon: CheckCircle, 
        text: "Terminée", 
        bg: "bg-green-100", 
        color: "text-green-800"
      },
      annule: { 
        icon: XCircle, 
        text: "Annulée", 
        bg: "bg-gray-100", 
        color: "text-gray-800"
      }
    };
    
    const config = configs[statut] || configs.nouveau;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.color}`}>
        <Icon size={12} className="mr-1" />
        {config.text}
      </span>
    );
  };

  const getPrioriteBadge = (priorite) => {
    const configs = {
      urgente: { 
        icon: AlertCircle, 
        text: "Urgente", 
        bg: "bg-red-100", 
        color: "text-red-800"
      },
      importante: { 
        icon: AlertCircle, 
        text: "Importante", 
        bg: "bg-orange-100", 
        color: "text-orange-800"
      },
      normale: { 
        icon: AlertCircle, 
        text: "Normale", 
        bg: "bg-blue-100", 
        color: "text-blue-800"
      }
    };
    
    const config = configs[priorite] || configs.normale;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.color}`}>
        <Icon size={12} className="mr-1" />
        {config.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy', { locale: fr });
    } catch (error) {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy à HH:mm', { locale: fr });
    } catch (error) {
      return dateString;
    }
  };

  const isTaskCompleted = (statut) => {
    return statut === 'termine' || statut === 'terminee';
  };

  const getActionLabel = (entry) => {
    if (entry.action === 'changement_statut') {
      const nouveauStatut = entry.statut_nouveau;
      const precedentStatut = entry.statut_precedent;
      
      if (nouveauStatut && precedentStatut) {
        return (
          <div className="flex items-center gap-2 flex-wrap">
            <span>Statut changé de</span>
            {getStatutBadge(precedentStatut)}
            <span>vers</span>
            {getStatutBadge(nouveauStatut)}
          </div>
        );
      } else if (nouveauStatut) {
        return (
          <div className="flex items-center gap-2">
            <span>Changement de statut vers</span>
            {getStatutBadge(nouveauStatut)}
          </div>
        );
      }
      
      return 'Changement de statut';
    }
    
    switch (entry.action) {
      case 'creation': return 'Tâche créée';
      case 'assignation': return 'Assignation modifiée';
      case 'commentaire': return 'Commentaire ajouté';
      case 'modification': return 'Tâche modifiée';
      default: return entry.action;
    }
  };

  if (!isOpen || !selectedTask) return null;

  const taskCompleted = isTaskCompleted(selectedTask.statut);
  const commentCount = selectedTask.commentaires?.length || 0;
  const historyCount = selectedTask.historique?.length || 0;
  const fileCount = selectedTask.fichiers?.length || 0;

  return createPortal(
    <div 
      className="fixed inset-0 bg-black/50 z-[9998]"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div className="w-full h-full overflow-y-auto flex items-center justify-center p-4">
        <div 
          className="bg-white rounded-lg shadow-2xl w-full max-w-6xl my-8 flex flex-col"
          style={{ maxHeight: 'calc(100vh - 4rem)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header - Fixed */}
          <div className="bg-[#246990] px-4 sm:px-5 py-3 rounded-t-lg flex items-center justify-between flex-shrink-0">
            <div className="flex-1 min-w-0">
              <h2 className="text-base sm:text-lg font-semibold text-white truncate">
                {selectedTask.titre}
              </h2>
              <div className="flex items-center gap-2 mt-1">
                {getStatutBadge(selectedTask.statut)}
                {getPrioriteBadge(selectedTask.priorite)}
              </div>
            </div>
            <button 
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-lg p-1.5 transition-colors ml-2 flex-shrink-0"
            >
              <X size={20} />
            </button>
          </div>

          {/* Body - Scrollable */}
          <div className="p-3 sm:p-5 overflow-y-auto flex-1">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-5">
              {/* Colonne gauche : Infos */}
              <div className="lg:col-span-3 space-y-3">
                {/* Informations générales */}
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-300">
                    <div className="w-7 h-7 bg-[#246990] rounded-lg flex items-center justify-center flex-shrink-0">
                      <FileText size={14} className="text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm">Informations</h3>
                  </div>
                  <div className="space-y-2 text-xs">
                    <div>
                      <p className="text-gray-500 mb-0.5">Statut</p>
                      <div className="mt-1">{getStatutBadge(selectedTask.statut)}</div>
                    </div>
                    <div>
                      <p className="text-gray-500 mb-0.5">Priorité</p>
                      <div className="mt-1">{getPrioriteBadge(selectedTask.priorite)}</div>
                    </div>
                    <div>
                      <p className="text-gray-500 mb-0.5">Créée par</p>
                      <p className="font-semibold text-gray-900">{selectedTask.createur_nom}</p>
                    </div>
                    {selectedTask.echeance && (
                      <div>
                        <p className="text-gray-500 mb-0.5">Échéance</p>
                        <p className="text-gray-700">{formatDate(selectedTask.echeance)}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Assignation */}
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-300">
                    <div className="w-7 h-7 bg-[#246990] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Users size={14} className="text-white" />
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm">Assignation</h3>
                  </div>
                  <div className="space-y-2 text-xs">
                    <div className="flex items-start gap-2">
                      <Users size={12} className="text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-500 mb-0.5">Groupe</p>
                        <p className="font-semibold text-gray-900 break-words">
                          {selectedTask.assigne_groupe}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <User size={12} className="text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-500 mb-0.5">Personne</p>
                        <p className="text-gray-700 break-words">
                          {selectedTask.assigne_employe_nom || "Tout le groupe"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Dates */}
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-blue-300">
                    <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Calendar size={14} className="text-white" />
                    </div>
                    <h3 className="font-semibold text-blue-900 text-sm">Dates</h3>
                  </div>
                  <div className="space-y-2 text-xs">
                    <div>
                      <p className="text-blue-600 mb-0.5">Créée le</p>
                      <p className="text-blue-900 font-medium">{formatDateTime(selectedTask.date_creation)}</p>
                    </div>
                    {selectedTask.date_modification && (
                      <div>
                        <p className="text-blue-600 mb-0.5">Modifiée le</p>
                        <p className="text-blue-900 font-medium">{formatDateTime(selectedTask.date_modification)}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Colonne droite : Contenu */}
              <div className="lg:col-span-9 space-y-4">
                {/* Description */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <FileText size={16} className="text-[#246990] flex-shrink-0" />
                    <h3 className="font-semibold text-gray-900 text-sm">Description</h3>
                  </div>
                  <div className="p-4 bg-gray-50 rounded border border-gray-200">
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {selectedTask.description}
                    </p>
                  </div>
                </div>

                {/* Fiche employé */}
                {selectedTask.type === 'creation_employe' && selectedTask.details_employe && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <User size={16} className="text-[#246990] flex-shrink-0" />
                      <h3 className="font-semibold text-gray-900 text-sm">Fiche de création d'employé</h3>
                    </div>
                    <div className="p-4 bg-purple-50 rounded border border-purple-200">
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                        <div>
                          <p className="text-gray-500 mb-0.5">Nom complet</p>
                          <p className="font-semibold text-gray-900">
                            {`${selectedTask.details_employe.prenom} ${selectedTask.details_employe.nom}`}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500 mb-0.5">Sexe</p>
                          <p className="text-gray-700">{selectedTask.details_employe.sexe}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 mb-0.5">Téléphone</p>
                          <p className="text-gray-700">{selectedTask.details_employe.telephone}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 mb-0.5">Poste</p>
                          <p className="text-gray-700">{selectedTask.details_employe.poste}</p>
                        </div>
                        <div>
                          <p className="text-gray-500 mb-0.5">Type de contrat</p>
                          <p className="text-gray-700">{selectedTask.details_employe.type_contrat}</p>
                        </div>
                        {selectedTask.details_employe.salaire && (
                          <div>
                            <p className="text-gray-500 mb-0.5">Salaire</p>
                            <p className="text-gray-700">{selectedTask.details_employe.salaire}€</p>
                          </div>
                        )}
                        <div>
                          <p className="text-gray-500 mb-0.5">Département</p>
                          <p className="text-gray-700">{selectedTask.details_employe.departement}</p>
                        </div>
                        {selectedTask.details_employe.date_embauche && (
                          <div>
                            <p className="text-gray-500 mb-0.5">Date d'embauche</p>
                            <p className="text-gray-700">{formatDate(selectedTask.details_employe.date_embauche)}</p>
                          </div>
                        )}
                      </div>
                      {selectedTask.details_employe.acces_application && (
                        <div className="mt-3 pt-3 border-t border-purple-200">
                          <div className="flex items-center gap-2 text-green-700">
                            <CheckCircle size={16} />
                            <span className="text-sm font-medium">Accès à l'application requis</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Fichiers joints */}
                {selectedTask.fichiers && selectedTask.fichiers.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Download size={16} className="text-[#246990] flex-shrink-0" />
                      <h3 className="font-semibold text-gray-900 text-sm">Fichiers joints ({fileCount})</h3>
                    </div>
                    <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
                      {selectedTask.fichiers.map((fichier, index) => (
                        <div key={index} className="flex items-center justify-between p-3 hover:bg-gray-50 transition-colors">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-8 h-8 bg-[#246990]/10 rounded flex items-center justify-center flex-shrink-0">
                              <FileText size={16} className="text-[#246990]" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-900 truncate">{fichier.nom_original}</p>
                              <p className="text-xs text-gray-500">{Math.round(fichier.taille_fichier / 1024)} KB</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDownloadFile(fichier.nom_fichier)}
                            className="ml-3 px-3 py-1.5 text-xs bg-[#246990] text-white rounded hover:bg-[#1d5373] transition-colors flex items-center gap-1.5"
                          >
                            <Download size={14} />
                            <span className="hidden sm:inline">Télécharger</span>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Historique */}
                {selectedTask.historique && selectedTask.historique.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Clock size={16} className="text-[#246990] flex-shrink-0" />
                      <h3 className="font-semibold text-gray-900 text-sm">Historique ({historyCount})</h3>
                    </div>
                    <div className="space-y-2">
                      {selectedTask.historique.map((entry, index) => (
                        <div key={index} className="p-3 bg-white border border-gray-200 rounded">
                          <div className="flex justify-between items-start mb-1">
                            <div className="text-sm font-medium text-gray-900">{getActionLabel(entry)}</div>
                            <span className="text-xs text-gray-500 ml-4 flex-shrink-0 bg-gray-100 px-2 py-0.5 rounded">
                              {formatDateTime(entry.date_action)}
                            </span>
                          </div>
                          {entry.commentaire && entry.commentaire.trim() !== '' && (
                            <p className="text-xs text-gray-600 mt-2 pl-3 border-l-2 border-gray-300">
                              "{entry.commentaire}"
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Commentaires */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <MessageSquare size={16} className="text-[#246990] flex-shrink-0" />
                    <h3 className="font-semibold text-gray-900 text-sm">Commentaires ({commentCount})</h3>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    {selectedTask.commentaires && selectedTask.commentaires.length > 0 ? (
                      selectedTask.commentaires.map((comment, index) => (
                        <div key={index} className="p-3 bg-white border border-gray-200 rounded">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 bg-[#246990] rounded-full flex items-center justify-center flex-shrink-0">
                                <User size={14} className="text-white" />
                              </div>
                              <span className="font-medium text-gray-900 text-sm">{comment.auteur_nom}</span>
                            </div>
                            <span className="text-xs text-gray-500">{formatDateTime(comment.date_creation)}</span>
                          </div>
                          <p className="text-sm text-gray-700 ml-9">{comment.commentaire}</p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded border border-gray-200">
                        Aucun commentaire pour le moment
                      </p>
                    )}
                  </div>
                  
                  {!taskCompleted ? (
                    <div className="p-3 bg-blue-50 rounded border border-blue-200">
                      <div className="space-y-2">
                        <div className="relative">
                          <Textarea
                            ref={textareaRef}
                            placeholder="Ajouter un commentaire..."
                            value={newCommentText}
                            onChange={handleTextChange}
                            rows={3}
                            className="resize-none text-sm w-full"
                            maxLength={500}
                          />
                          <div className="absolute bottom-2 right-2 text-xs text-gray-400 bg-white px-2 py-1 rounded">
                            {newCommentText.length}/500
                          </div>
                        </div>
                        <button
                          onClick={handleAddComment}
                          disabled={!newCommentText.trim()}
                          className="w-full px-4 py-2 text-sm bg-[#246990] text-white rounded hover:bg-[#1d5373] transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <MessageSquare className="w-4 h-4" />
                          Ajouter un commentaire
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2 p-3 bg-gray-100 border border-gray-300 rounded">
                      <Lock className="text-gray-500 flex-shrink-0" size={18} />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Commentaires verrouillés</p>
                        <p className="text-xs text-gray-600 mt-0.5">Cette tâche est terminée</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer - Fixed */}
          <div className="bg-gray-50 px-3 sm:px-5 py-3 rounded-b-lg border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-3 flex-shrink-0">
            <p className="text-xs text-gray-600 text-center sm:text-left">
              Consultation de la tâche <strong className="text-gray-900">{selectedTask.titre}</strong>
            </p>
            <div className="flex gap-2 w-full sm:w-auto">
              <button
                onClick={onClose}
                className="flex-1 sm:flex-none px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TaskDetailsModal;